import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Key, Plus, Trash2, Copy, ChevronRight, AlertCircle, Loader2, X } from 'lucide-react';
import api from '../../services/api';
import PageHeader from './PageHeader';
import EmptyState from '../ui/EmptyState';

// Dialog Component
const Dialog = ({ open, onOpenChange, children }) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-gray-950/40 dark:bg-black/70"
                onClick={() => onOpenChange(false)}
            />
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative z-50 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
            >
                {children}
            </motion.div>
        </div>
    );
};

// Alert Component
const Alert = ({ children, className = "" }) => (
    <div className={`flex items-center gap-2 rounded-lg border border-danger-500/25 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400 ${className}`}>
        {children}
    </div>
);

const ApiKeyManagement = () => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNewKeyModal, setShowNewKeyModal] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyData, setNewKeyData] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchKeys();
    }, []);

    useEffect(() => {
        console.log('Current keys:', keys);
    }, [keys]);

    const fetchKeys = async () => {
        try {
            const response = await api.get('/api/api-keys/');
            console.log('API Response:', response.data);  
            setKeys(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch API keys');
            console.error('Error fetching keys:', err);
        } finally {
            setLoading(false);
        }
    };

    const createKey = async () => {
        try {
            const response = await api.post('/api/api-keys/create/', {
                name: newKeyName || undefined
            });
            setNewKeyData(response.data);
            await fetchKeys();
            setNewKeyName('');
        } catch (err) {
            setError('Failed to create API key');
            console.error('Error creating key:', err);
        }
    };

    const deleteKey = async (id) => {
        setDeletingId(id);
        console.log('Deleting key with ID:', id); 
        try {
            await api.delete(`/api/api-keys/${id}/delete/`);
            await fetchKeys();
        } catch (err) {
            console.error('Error response:', err.response);
            setError('Failed to delete API key');
            console.error('Error deleting key:', err);
        } finally {
            setDeletingId(null);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
        );
    }

    return (
        <div>
            <div className="max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="mb-8"
                >
                    <PageHeader icon={Key} title="API Keys">
                        <button
                            onClick={() => {
                                setShowNewKeyModal(true);
                                setNewKeyData(null);
                            }}
                            className="btn btn-primary"
                        >
                            <Plus className="h-4 w-4" />
                            Create New Key
                        </button>
                    </PageHeader>

                    {error && (
                        <Alert className="mb-6">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <span>{error}</span>
                        </Alert>
                    )}

                    <AnimatePresence>
                        {keys.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="card"
                            >
                                <EmptyState
                                    icon={Key}
                                    title="No API Keys Found"
                                    description="Create your first API key to get started"
                                />
                            </motion.div>
                        ) : (
                            <div className="card overflow-hidden">
                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {keys.map((key, index) => (
                            <motion.div
                                key={key.id}  // Use the shorter id (prefix) for the React key
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
                            >
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                                        <Key className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{key.name}</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Created {formatDate(key.created)}</p>
                                    </div>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                    {deletingId === key.id ? (  // Use the same id (prefix) here
                                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                    ) : (
                                        <button
                                            onClick={() => deleteKey(key.id)}  // Use the id (prefix) for deletion
                                            aria-label="Delete API key"
                                            title="Delete API key"
                                            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                    <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                                </div>
                            </motion.div>
                        ))}
                        </div>
                    </div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>

            <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
                <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
                    <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
                        <Key className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                        Create New API Key
                    </h2>
                    <button
                        onClick={() => setShowNewKeyModal(false)}
                        className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    >
                        <X className="h-[18px] w-[18px]" />
                    </button>
                </div>
                <div className="px-6 pb-6 pt-1">
                    <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                        Enter a name for your new API key. This will help you identify its usage later.
                    </p>

                    {!newKeyData ? (
                        <div>
                            <input
                                type="text"
                                placeholder="API Key Name"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                className="input"
                            />
                            <div className="-mx-6 -mb-6 mt-6 flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
                                <button
                                    onClick={() => setShowNewKeyModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createKey}
                                    className="btn btn-primary"
                                >
                                    Create Key
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="break-all rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                <p className="font-mono text-sm text-gray-800 dark:text-gray-200">{newKeyData.key}</p>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <p className="text-sm text-warning-700 dark:text-warning-400">
                                    Make sure to copy your API key now. You won't be able to see it again!
                                </p>
                                <button
                                    onClick={() => copyToClipboard(newKeyData.key)}
                                    className="btn btn-primary flex-shrink-0"
                                >
                                    <Copy className="h-4 w-4" />
                                    <span>{copySuccess ? 'Copied!' : 'Copy'}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default ApiKeyManagement;